// The account self-service REST client: a signed-in user managing their OWN sessions (the G5 slice). Hits
// `${authority}account/...` with the user's Bearer token. Behind `IAccountApi` for fake-based ViewModel tests.

import { token } from '@/infrastructure/container';
import { type IAuth } from '@/services/AuthService';
import { ServerError } from '@/domain/errors';
import { type AdminSession } from '@/domain/admin';
import { type MfaStatus, type TotpStart } from '@/domain/mfa';

export interface IAccountApi {
  listSessions(): Promise<AdminSession[]>;
  logoutOne(sessionId: string): Promise<void>;
  logoutAll(): Promise<void>;
  /** Change the caller's own password (re-auth with the current). On success every session is revoked, so the
   * caller must sign in again. */
  changePassword(currentPassword: string, newPassword: string): Promise<void>;

  // --- MFA (U1). Every factor MUTATION is re-auth-gated server-side; enroll/remove also revoke
  // every session (the M2 watermark rule), so the caller must sign in again afterwards.
  mfaStatus(): Promise<MfaStatus>;
  totpStart(currentPassword: string): Promise<TotpStart>;
  /** Returns the one-time recovery codes when this enrollment is the account's FIRST factor; null otherwise. */
  totpConfirm(enrollTicket: string, code: string): Promise<string[] | null>;
  totpRemove(currentPassword: string): Promise<void>;
  /** A fresh batch REPLACES the old set; shown once. The server refuses without an enrolled factor. */
  regenerateRecoveryCodes(currentPassword: string): Promise<string[]>;
  /** Returns the webauthn-rs creation challenge (opaque to the store; the ceremony service consumes it). */
  webauthnStart(currentPassword: string): Promise<{ ccr: unknown; reg_ticket: string }>;
  /** Returns the one-time recovery codes when this is the FIRST factor; null otherwise. */
  webauthnFinish(regTicket: string, name: string, credential: unknown): Promise<string[] | null>;
  webauthnRemove(credId: string, currentPassword: string): Promise<void>;
}

export const TOKEN_IAccountApi = token<IAccountApi>('IAccountApi');

export class AccountApi implements IAccountApi {
  constructor(private auth: IAuth) {}

  private base(): string {
    return `${this.auth.config.authority}account`;
  }

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const user = await this.auth.currentUser();
    if (!user) throw new ServerError('not signed in');
    const res = await fetch(`${this.base()}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${user.accessToken}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    if (!res.ok) {
      // Surface the server's error code (e.g. invalid_credentials, weak_password) so the ViewModel can show it.
      let msg = `${res.status} ${res.statusText}`;
      try {
        const j = JSON.parse(text) as { error?: string; error_description?: string };
        if (j.error_description || j.error) msg = j.error_description ?? j.error ?? msg;
      } catch {
        if (text) msg = text.slice(0, 200);
      }
      throw new ServerError(msg);
    }
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  async listSessions(): Promise<AdminSession[]> {
    const r = await this.req<{ sessions: AdminSession[] }>('GET', '/sessions');
    return r.sessions;
  }
  async logoutOne(sessionId: string): Promise<void> {
    await this.req('DELETE', `/sessions/${encodeURIComponent(sessionId)}`);
  }
  async logoutAll(): Promise<void> {
    await this.req('POST', '/logout-all');
  }
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.req('POST', '/password', { current_password: currentPassword, new_password: newPassword });
  }

  async mfaStatus(): Promise<MfaStatus> {
    return await this.req<MfaStatus>('GET', '/mfa');
  }
  async totpStart(currentPassword: string): Promise<TotpStart> {
    return await this.req<TotpStart>('POST', '/mfa/totp/start', { current_password: currentPassword });
  }
  async totpConfirm(enrollTicket: string, code: string): Promise<string[] | null> {
    const r = await this.req<{ recovery_codes: string[] | null }>('POST', '/mfa/totp/confirm', {
      enroll_ticket: enrollTicket,
      code,
    });
    return r.recovery_codes;
  }
  async totpRemove(currentPassword: string): Promise<void> {
    await this.req('DELETE', '/mfa/totp', { current_password: currentPassword });
  }
  async regenerateRecoveryCodes(currentPassword: string): Promise<string[]> {
    const r = await this.req<{ recovery_codes: string[] }>('POST', '/mfa/recovery-codes/regenerate', {
      current_password: currentPassword,
    });
    return r.recovery_codes;
  }
  async webauthnStart(currentPassword: string): Promise<{ ccr: unknown; reg_ticket: string }> {
    return await this.req<{ ccr: unknown; reg_ticket: string }>('POST', '/mfa/webauthn/start', {
      current_password: currentPassword,
    });
  }
  async webauthnFinish(regTicket: string, name: string, credential: unknown): Promise<string[] | null> {
    const r = await this.req<{ recovery_codes: string[] | null }>('POST', '/mfa/webauthn/finish', {
      reg_ticket: regTicket,
      name,
      credential,
    });
    return r.recovery_codes;
  }
  async webauthnRemove(credId: string, currentPassword: string): Promise<void> {
    await this.req('DELETE', `/mfa/webauthn/${encodeURIComponent(credId)}`, { current_password: currentPassword });
  }
}

// In-memory fake for ViewModel tests. The MFA half models the SERVER's semantics: re-auth-gated
// mutations (a wrong `password` rejects), first-factor enrollment mints the one-time recovery
// codes, regenerate replaces the batch and refuses with no factor enrolled.
export class FakeAccountApi implements IAccountApi {
  sessions: AdminSession[] = [];
  logoutAllCalls = 0;
  changePasswordCalls: { current: string; next: string }[] = [];
  failNext: Error | null = null;

  /** The password the fake's re-auth gate accepts. */
  password = 'correct-password';
  totpEnrolled = false;
  webauthn: { cred_id: string; name: string; added_ts: number }[] = [];
  recoveryRemaining = 0;
  /** The pending TOTP enrollment minted by `totpStart`; `totpConfirm` requires its ticket + this code. */
  pendingTotp: TotpStart | null = null;
  totpGoodCode = '123456';
  finishCalls: { reg_ticket: string; name: string; credential: unknown }[] = [];

  private maybeThrow(): void {
    if (this.failNext) {
      const e = this.failNext;
      this.failNext = null;
      throw e;
    }
  }

  private reauth(currentPassword: string): void {
    if (currentPassword !== this.password) throw new ServerError('invalid_credentials');
  }

  private get enabled(): boolean {
    return this.totpEnrolled || this.webauthn.length > 0;
  }

  /** First factor overall -> a fresh one-time batch rides the enroll response (the server rule). */
  private maybeFirstFactorCodes(wasEnabled: boolean): string[] | null {
    if (wasEnabled) return null;
    this.recoveryRemaining = 10;
    return Array.from({ length: 10 }, (_, i) => `aaaaa-0000${i}`);
  }
  async listSessions(): Promise<AdminSession[]> {
    this.maybeThrow();
    return [...this.sessions];
  }
  async logoutOne(sessionId: string): Promise<void> {
    this.maybeThrow();
    this.sessions = this.sessions.filter((s) => s.session_id !== sessionId);
  }
  async logoutAll(): Promise<void> {
    this.maybeThrow();
    this.logoutAllCalls++;
    this.sessions = [];
  }
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    this.maybeThrow();
    this.changePasswordCalls.push({ current: currentPassword, next: newPassword });
    this.sessions = []; // the change revokes every session
  }

  async mfaStatus(): Promise<MfaStatus> {
    this.maybeThrow();
    return {
      totp_enabled: this.totpEnrolled,
      recovery_codes_remaining: this.recoveryRemaining,
      webauthn: [...this.webauthn],
    };
  }
  async totpStart(currentPassword: string): Promise<TotpStart> {
    this.maybeThrow();
    this.reauth(currentPassword);
    this.pendingTotp = {
      secret: 'JBSWY3DPEHPK3PXP',
      otpauth_uri: 'otpauth://totp/test:user?secret=JBSWY3DPEHPK3PXP',
      enroll_ticket: 'ticket-1',
    };
    return this.pendingTotp;
  }
  async totpConfirm(enrollTicket: string, code: string): Promise<string[] | null> {
    this.maybeThrow();
    if (!this.pendingTotp || enrollTicket !== this.pendingTotp.enroll_ticket) throw new ServerError('enroll_expired');
    if (code !== this.totpGoodCode) throw new ServerError('bad_code');
    const was = this.enabled;
    this.totpEnrolled = true;
    this.pendingTotp = null;
    return this.maybeFirstFactorCodes(was);
  }
  async totpRemove(currentPassword: string): Promise<void> {
    this.maybeThrow();
    this.reauth(currentPassword);
    this.totpEnrolled = false;
  }
  async regenerateRecoveryCodes(currentPassword: string): Promise<string[]> {
    this.maybeThrow();
    this.reauth(currentPassword);
    if (!this.enabled) throw new ServerError('enroll an authenticator before generating recovery codes');
    this.recoveryRemaining = 10;
    return Array.from({ length: 10 }, (_, i) => `bbbbb-0000${i}`);
  }
  async webauthnStart(currentPassword: string): Promise<{ ccr: unknown; reg_ticket: string }> {
    this.maybeThrow();
    this.reauth(currentPassword);
    return { ccr: { publicKey: { challenge: 'abc' } }, reg_ticket: 'wa-ticket-1' };
  }
  async webauthnFinish(regTicket: string, name: string, credential: unknown): Promise<string[] | null> {
    this.maybeThrow();
    if (regTicket !== 'wa-ticket-1') throw new ServerError('enroll_expired');
    this.finishCalls.push({ reg_ticket: regTicket, name, credential });
    const was = this.enabled;
    this.webauthn.push({ cred_id: `cred-${this.webauthn.length + 1}`, name, added_ts: 1000 });
    return this.maybeFirstFactorCodes(was);
  }
  async webauthnRemove(credId: string, currentPassword: string): Promise<void> {
    this.maybeThrow();
    this.reauth(currentPassword);
    this.webauthn = this.webauthn.filter((c) => c.cred_id !== credId);
  }
}
